import { useAsyncData } from '#imports'
import { type MaybeRefOrGetter, computed, toRef } from 'vue'

import type { RecommendationsQuery } from '@algolia/recommend'
import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import algoliasearch from 'algoliasearch'
import type { AsyncDataOptions } from 'nuxt/app'

import {
  algoliaHitToBackbox,
  algoliaHitToVariant,
} from '../../helpers/recommendationHelper'
import type { Product } from '../../models/product'
import { useFallbackParameters } from '../useFallbackParameters/useFallbackParameters'
import { useHttpFetchLoggers } from '../useHttpFetchLoggers'

import type {
  AlgoliaRecommendationsModel,
  AlgoliaRecommendationsObjectIds,
  AlgoliaRecommendationsOptions,
} from './useAlgoliaRecommendations.types'
import { sortAndMergeRecommendations } from './useAlgoliaRecommendations.utils'

const MAX_OBJECT_IDS = 10

export function useAlgoliaRecommendations(
  objectIds: MaybeRefOrGetter<AlgoliaRecommendationsObjectIds>,
  model: AlgoliaRecommendationsModel,
  options?: MaybeRefOrGetter<AlgoliaRecommendationsOptions | undefined>,
  asyncOptions?: Pick<AsyncDataOptions<Product[]>, 'server'>,
) {
  const objectIdsRef = toRef(objectIds)
  const optionsRef = toRef(options)

  const $httpFetch = useHttpFetch()
  const { getFallbackTypeFromRuleContexts, fetchFallbackParameters } =
    useFallbackParameters()
  const algoliaSearchOption = useAlgoliaSearchOptions('recommendations')
  const algoliaClient = algoliasearch(
    'dummy_app_id',
    'dummy_key',
    algoliaSearchOption,
  )
  const logger = useLogger()

  const { data: configurationData, execute: executeConfiguration } =
    useAsyncData(
      'algoliaRecommendationsConfig',
      () =>
        $httpFetch('/bm/recommendation/configuration', {
          ...useHttpFetchLoggers(),
        }),
      { dedupe: 'defer', immediate: false },
    )

  const queryKey = computed(() =>
    getAsyncDataKey('algoliaRecommendations', {
      objectIds: objectIdsRef.value,
      model,
      ...optionsRef.value,
    }),
  )

  return useAsyncData(
    queryKey,
    async () => {
      if (!configurationData.value?.indexName) {
        await executeConfiguration()
      }

      if (
        !objectIdsRef.value ||
        objectIdsRef.value.length === 0 ||
        !configurationData.value?.indexName
      )
        return null

      const { indexName } = configurationData.value

      if (objectIdsRef.value.length > MAX_OBJECT_IDS) {
        logger.info(
          `[S&R] Too many objectIds (${objectIdsRef.value.length}), this may cause latency issues`,
          {
            type: 'WARNING',
            endpointName: 'recommendationFetch',
            objectIdsCount: objectIdsRef.value.length,
            owners: ['bot-squad-search-and-recommendation-front'],
          },
        )
      }

      const fallbackType = getFallbackTypeFromRuleContexts(
        optionsRef.value?.queryParameters?.ruleContexts,
      )

      const fallbackParams =
        model === 'bought-together'
          ? await fetchFallbackParameters(objectIdsRef.value)
          : undefined

      const requests: RecommendationsQuery[] = objectIdsRef.value.map(
        (objectId) => {
          const filters = fallbackParams?.[objectId]?.[fallbackType]

          return {
            indexName,
            objectID: objectId,
            model,
            ...optionsRef.value,
            ...(filters && { fallbackParameters: { filters } }),
          }
        },
      )

      const startTime = performance.now()

      try {
        const result =
          await algoliaClient.getRecommendations<AlgoliaProduct>(requests)

        logger.info('[S&R] Recommendation query succeeded', {
          type: 'API_REQUEST_SUCCESS',
          endpointName: 'recommendationFetch',
          elapsedTime: Math.floor(performance.now() - startTime),
          statusCode: 200,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        return result
      } catch (e) {
        logger.error('[S&R] Recommendation query failed', {
          type: 'API_REQUEST_FAIL',
          endpointName: 'recommendationFetch',
          elapsedTime: Math.floor(performance.now() - startTime),
          statusCode: (e as { status?: number })?.status || 500,
          e,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        throw e
      }
    },
    {
      lazy: true,
      dedupe: 'defer',
      default: () => [] as Product[],
      transform: (response) => {
        if (!response) {
          return []
        }

        const { results } = response
        const allHits = results.map((result) => result.hits)
        const mergedHits = sortAndMergeRecommendations<AlgoliaProduct>(
          allHits,
          objectIdsRef.value ?? [],
        )

        const limitedHits = optionsRef.value?.maxRecommendations
          ? mergedHits.slice(0, optionsRef.value?.maxRecommendations)
          : mergedHits

        return limitedHits.map(
          model === 'bought-together'
            ? algoliaHitToBackbox
            : algoliaHitToVariant,
        )
      },
      ...asyncOptions,
    },
  )
}
